// Bảng hàng hóa của biên bản trả hàng. API luôn trả ĐỦ dòng của hóa đơn; người
// lập xóa bớt dòng không trả (xóa nhầm thì hoàn tác ngay tại dòng đó) và sửa
// số lượng trả của từng dòng.
// Số tiền do BE tính lại (agreement-gra-preview), bảng này không tự tính.
import { validateReturnQuantity } from "pages/IssueInvoice/helper/agreementReport";
import PropTypes from "prop-types";
import React from "react";
import NumberFormat from "react-number-format";
import { Button, Input } from "reactstrap";

const COLUMNS = [
  { key: "stt", label: "STT", className: "text-center", width: "60px" },
  { key: "name", label: "Tên hàng hóa, dịch vụ" },
  {
    key: "unit",
    label: "Đơn vị tính",
    className: "text-center",
    width: "110px",
  },
  {
    key: "quantity",
    label: "Số lượng",
    className: "text-end",
    width: "150px",
    isNumber: true,
  },
  {
    key: "price",
    label: "Đơn giá",
    className: "text-end",
    width: "150px",
    isNumber: true,
  },
  {
    key: "amount",
    label: "Thành tiền",
    className: "text-end",
    width: "150px",
    isNumber: true,
  },
];

const renderCell = (item, column) => {
  const value = item?.[column.key];

  if (!column.isNumber) {
    return value ?? "";
  }

  // API trả số dưới dạng chuỗi; chuỗi rỗng/không phải số thì để trống
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return (
    <NumberFormat
      value={value}
      displayType="text"
      thousandSeparator={true}
      decimalScale={2}
    />
  );
};

const AgreementItemsTable = props => {
  const {
    items,
    selectedIds,
    onChangeSelected,
    quantities,
    onChangeQuantity,
    amountsById,
    error,
    isDisabled,
    isReadOnly,
  } = props;

  if (!(items?.length > 0)) {
    return (
      <div className="text-muted text-center py-2">
        Hóa đơn không có dòng hàng hóa nào
      </div>
    );
  }

  const isKept = item =>
    selectedIds.some(id => Number(id) === Number(item?.id));
  const keptCount = items.filter(isKept).length;

  const removeItem = item =>
    onChangeSelected(selectedIds.filter(id => Number(id) !== Number(item?.id)));

  // Thêm lại đúng vị trí cũ: giữ thứ tự dòng của hóa đơn
  const restoreItem = item =>
    onChangeSelected(
      items
        .filter(row => isKept(row) || Number(row?.id) === Number(item?.id))
        .map(row => row.id)
    );

  // Ô số lượng: chỉ dòng đang giữ và biên bản chưa chốt
  const renderQuantityCell = (item, column, kept) => {
    if (isReadOnly || !kept) {
      return renderCell(item, column);
    }

    const value = quantities?.[Number(item.id)] ?? item.quantity;
    const error = validateReturnQuantity(value, item.quantity);

    return (
      <React.Fragment>
        <Input
          type="number"
          bsSize="sm"
          className="text-end"
          min={1}
          step={1}
          value={value}
          invalid={Boolean(error)}
          disabled={isDisabled}
          onChange={event => onChangeQuantity(item.id, event.target.value)}
        />
        {error && (
          <div className="text-danger font-size-12 text-start">{error}</div>
        )}
      </React.Fragment>
    );
  };

  // Thành tiền: dòng đang giữ lấy số BE vừa tính lại theo số lượng trả
  const renderAmountCell = (item, column, kept) => {
    const previewAmount = kept ? amountsById?.[Number(item.id)] : undefined;
    return renderCell(
      previewAmount === undefined ? item : { ...item, amount: previewAmount },
      column
    );
  };

  const renderBodyCell = (item, column, kept) => {
    if (column.key === "quantity") {
      return renderQuantityCell(item, column, kept);
    }
    if (column.key === "amount") {
      return renderAmountCell(item, column, kept);
    }
    return renderCell(item, column);
  };

  // STT đánh lại theo các dòng còn giữ, khớp với PDF
  let keptIndex = 0;

  return (
    <React.Fragment>
      <div className="table-responsive">
        <table className="table table-bordered align-middle mb-0">
          <thead className="table-light">
            <tr>
              {COLUMNS.map(column => (
                <th
                  key={column.key}
                  className={column.className}
                  style={column.width ? { width: column.width } : undefined}
                >
                  {column.label}
                </th>
              ))}
              {/* Biên bản đã chốt thì không xóa / hoàn tác được nữa, bỏ luôn cột */}
              {!isReadOnly && (
                <th className="text-center" style={{ width: "90px" }}>
                  Thao tác
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => {
              const kept = isKept(item);
              if (kept) {
                keptIndex += 1;
              }

              return (
                <tr
                  key={item?.id || item?.stt || index}
                  className={kept ? "" : "text-muted"}
                  style={kept ? undefined : { backgroundColor: "#f8f9fa" }}
                >
                  {COLUMNS.map(column => (
                    <td
                      key={column.key}
                      className={column.className}
                      style={{
                        whiteSpace: "pre-wrap",
                        textDecoration: kept ? "none" : "line-through",
                      }}
                    >
                      {column.key === "stt"
                        ? kept
                          ? keptIndex
                          : ""
                        : renderBodyCell(item, column, kept)}
                    </td>
                  ))}
                  {!isReadOnly && (
                    <td className="text-center">
                      {kept ? (
                        <Button
                          type="button"
                          color="link"
                          className="p-0 text-danger"
                          // Biên bản phải giữ lại ít nhất 1 dòng hàng hóa
                          disabled={isDisabled || keptCount <= 1}
                          title={
                            keptCount <= 1
                              ? "Phải giữ lại ít nhất 1 dòng hàng hóa"
                              : "Xóa dòng khỏi biên bản"
                          }
                          onClick={() => removeItem(item)}
                        >
                          <i className="bx bx-trash font-size-18" />
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          color="link"
                          className="p-0"
                          disabled={isDisabled}
                          title="Hoàn tác dòng đã xóa"
                          onClick={() => restoreItem(item)}
                        >
                          <i className="bx bx-undo font-size-18" />
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {keptCount < items.length && (
        <div className="text-muted font-size-12 mt-2">
          Đã bỏ {items.length - keptCount}/{items.length} dòng khỏi biên bản.
          {!isReadOnly && " Bấm hoàn tác ở dòng mờ để lấy lại."}
        </div>
      )}
      {/* Lỗi BE trả về khi tính tiền / lưu (vd. số lượng trả vượt hóa đơn) */}
      {error && (
        <div
          className="d-flex align-items-center text-danger border border-danger rounded px-3 py-2 mt-3"
          style={{ backgroundColor: "rgba(244, 106, 106, 0.08)" }}
          role="alert"
        >
          <i className="bx bxs-error-circle font-size-16 me-1 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </React.Fragment>
  );
};

AgreementItemsTable.propTypes = {
  items: PropTypes.array,
  // Id các dòng GIỮ LẠI (item_ids gửi cho BE)
  selectedIds: PropTypes.array,
  onChangeSelected: PropTypes.func,
  // Số lượng trả đang nhập { [id]: số lượng } và hàm đổi số lượng (id, giá trị)
  quantities: PropTypes.object,
  onChangeQuantity: PropTypes.func,
  // Thành tiền BE tính lại theo số lượng trả { [id]: amount }
  amountsById: PropTypes.object,
  // Câu lỗi BE trả về, hiện ngay dưới bảng
  error: PropTypes.string,
  // Khóa tạm thời trong lúc gọi API, nút vẫn hiện
  isDisabled: PropTypes.bool,
  // Biên bản đã chốt: bỏ hẳn cột thao tác
  isReadOnly: PropTypes.bool,
};

AgreementItemsTable.defaultProps = {
  items: [],
  selectedIds: [],
  onChangeSelected: () => {},
  quantities: {},
  onChangeQuantity: () => {},
  amountsById: {},
  isDisabled: false,
  isReadOnly: false,
};

export default AgreementItemsTable;
